<template>
  <div>
    <v-snackbar
      :model-value="snackbarNotificationDisplayed"
      location="bottom"
      :timeout="snackbarTimeout"
      class="notification-snackbar"
      @update:model-value="snackbarNotificationDisplayed = $event"
    >
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center flex-shrink-1">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="snackbarNotificationMessage" />
          <the-button
            v-if="snackbarNotificationCustomButton"
            text
            small
            class="my-0 pa-0 ml-1 color-post-background"
            custom-colors
            wide
            :to="snackbarNotificationCustomButton.route"
            @click="hideSnackbarNotification"
          >
            {{ snackbarNotificationCustomButton.label }}
          </the-button>
        </div>

        <the-button
          v-if="snackbarNotificationCustomCloseButton"
          text
          small
          class="my-0 ml-4 color-post-background"
          custom-colors
          @click="onCustomNotificationCloseButtonClick"
        >
          {{ snackbarNotificationCustomCloseButton.label }}
        </the-button>
        <the-button v-else text small class="my-0 ml-4 color-post-background" custom-colors @click="hideSnackbarNotification">
          {{ $translate("common.close") }}
        </the-button>
      </div>
    </v-snackbar>

    <!--offset prop not working-->
    <v-snackbar
      :model-value="snackbarErrorDisplayed"
      location="bottom"
      :timeout="10000"
      class="error-snackbar"
      :style="{ 'margin-bottom': `${snackbarErrorOffset}px` }"
      @update:model-value="snackbarErrorDisplayed = $event"
    >
      <div class="d-flex align-center justify-space-between gap-4">
        <span>{{ snackbarErrorMessage }}</span>
        <the-button
          v-if="snackbarErrorContactSupportButtonDisplayed"
          text
          custom-colors
          :href="`mailto:${configuration.supportEmail}`"
          @click="hideSnackbarNotification"
        >
          {{ $translate("SnackbarsHub.contactSupport") }}
        </the-button>
        <the-button icon class="error-close-button" custom-colors @click="hideSnackbarError">
          <i-custom-close />
        </the-button>
      </div>
    </v-snackbar>

    <v-snackbar
      :model-value="livestreamSnackbarDisplayed"
      :timeout="-1"
      class="livestream-snackbar cursor-pointer"
      :class="{ desktop: !$vuetify.display.mobile }"
      @click="$router.push({ name: $routes.LIVE })"
    >
      <div class="d-flex align-center" :class="{ 'gap-3': $vuetify.display.mobile, 'gap-4': !$vuetify.display.mobile }">
        <template v-if="livestreamEvent">
          <v-img v-if="livestreamEventImage" :src="livestreamEventImage" cover :max-height="64" :min-width="64" :max-width="64" class="rounded-app" />
          <div v-else class="event-default-thumbnail d-flex align-center justify-center background-post-detail-15 rounded-app">
            <i-custom-calendar class="size-8 color-post-detail" />
          </div>
        </template>
        <v-img v-else-if="livestreamGroup?.imageUrl" :src="livestreamGroup.imageUrl" cover :height="64" :min-width="96" :max-width="96" class="rounded-app" />
        <div class="flex-grow-1">
          <div class="livestream-title truncate-multiline-1 mb-2 font-weight-bold font-size-big">
            <div v-if="livestreamEvent">{{ livestreamEvent.title }}</div>
            <div v-else>
              {{ livestreamPreparing ? $translate("SnackbarsHub.livestreamStartingSoon") : $translate("SnackbarsHub.livestreamingNow") }}
            </div>
          </div>
          <div class="d-flex align-center gap-2">
            <div
              class="state-container rounded-app py-1 px-2 border-post-detail d-flex align-center flex-shrink-0"
              :class="{ 'gap-1': $vuetify.display.mobile, 'gap-2': !$vuetify.display.mobile }"
            >
              <i-custom-dot class="live-dot size-2" />
              <div class="font-weight-bold color-post-detail">
                {{ livestreamPreparing ? $translate("common.standBy") : $translate("common.live") }}
              </div>
            </div>
            <div v-if="livestreamEvent">{{ $translate("SnackbarsHub.joinNow") }}</div>
            <div v-else class="truncate-multiline-2">{{ livestreamGroup?.name }}</div>
          </div>
        </div>

        <the-button icon small custom-colors class="background-post-detail flex-shrink-1" @click.stop="dismissLivestreamSnackbar">
          <i-custom-close class="size-3" />
        </the-button>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "pinia"

import { useAppStateStore } from "@/store/appState.js"
import { useUserSessionStore } from "@/store/userSession.js"
import { useGroupsStore } from "@/store/groups.js"
import { useConfigurationStore } from "@/store/configuration.js"

import * as images from "@/services/images"
import { sleep } from "@/services/concurrency.js"

import { eventTypes } from "@/constants/livestreamProviders.js"

import * as eventsApi from "@/api/eventsApi.js"

import DOMPurify from "dompurify"

export default {
  data() {
    return {
      snackbarTimeout: 3000,
      snackbarNotificationDisplayed: false,
      snackbarNotificationMessage: null,
      snackbarNotificationCustomButton: null,
      snackbarNotificationCustomCloseButton: null,
      snackbarErrorDisplayed: false,
      snackbarErrorMessage: null,
      snackbarErrorOffset: null,
      snackbarErrorContactSupportButtonDisplayed: null,
      shouldDisplayPaywallSuccessDialog: false,
      snackbarRelatedLivestream: null,
      manuallyDismissedLivestreamIds: [],
      dismissedForOtherSnackbarsLivestreamIds: [],
      livestreamEvent: null
    }
  },
  computed: {
    ...mapGetters(useAppStateStore, ["webViewMode"]),
    ...mapGetters(useConfigurationStore, ["configuration"]),
    ...mapGetters(useUserSessionStore, ["user", "isLoggedIn", "ownedPaywallPlan"]),
    ...mapGetters(useGroupsStore, ["groupForKey", "isUserMember"]),
    isAppLockedForUser() {
      const subscriptionRequired = this.configuration.isPremium && !this.ownedPaywallPlan

      const legalDocumentsAcceptanceNeeded =
        this.configuration.privacyPolicyVersion > this.user.agreed_legal.privacy_policy_version ||
        this.configuration.termsAndConditionsVersion > this.user.agreed_legal.terms_conditions_version

      return this.isLoggedIn && (!this.user.onboarding_completed || subscriptionRequired || legalDocumentsAcceptanceNeeded)
    },
    livestreamGroup() {
      if (!this.snackbarRelatedLivestream) {
        return null
      }

      return this.groupForKey(this.snackbarRelatedLivestream.groupKey)
    },
    livestreamPreparing() {
      return this.snackbarRelatedLivestream?.state === eventTypes.preparing
    },
    livestreamEventImage() {
      const image = this.livestreamEvent?.images[0]
      if (!image) {
        return null
      }

      return images.getBestImageVersion(image, { type: "width", minWidth: 100 }).url
    },
    livestreamSnackbarDisplayed() {
      return this.isLoggedIn && Boolean(this.snackbarRelatedLivestream) && this.$route.name !== this.$routes.LIVE
    }
  },
  created() {
    this.$eventBus.$on("notify", this.showSnackbarNotification)
    this.$eventBus.$on("snackbar-error", this.showSnackbarError)
    this.$eventBus.$on("pn-livestream-preparing", this.notifyLivestreamPreparing)
    this.$eventBus.$on("pn-livestream-streaming", this.notifyLivestreamStreaming)
    this.$eventBus.$on("pn-livestream-ended", this.hideLivestreamSnackbar)
  },
  mounted() {
    if (this.$routerState.wasMagicLinkRequestForAuthenticatedUser) {
      this.showSnackbarNotification(this.$translate("SnackbarsHub.alreadyLoggedIn"))
    }
  },
  beforeUnmount() {
    this.$eventBus.$off("notify", this.showSnackbarNotification)
    this.$eventBus.$off("snackbar-error", this.showSnackbarError)
    this.$eventBus.$off("pn-livestream-preparing", this.notifyLivestreamPreparing)
    this.$eventBus.$off("pn-livestream-streaming", this.notifyLivestreamStreaming)
    this.$eventBus.$off("pn-livestream-ended", this.hideLivestreamSnackbar)
  },
  methods: {
    async showSnackbarNotification(data) {
      let message,
        timeout = 3000,
        customButton,
        customCloseButton
      if (typeof data === "string") {
        message = data
      } else {
        message = data.message
        timeout = data.timeout || 3000
        customButton = data.customButton
        customCloseButton = data.customCloseButton
      }

      this.snackbarTimeout = timeout
      this.snackbarErrorDisplayed = false
      this.snackbarNotificationDisplayed = false
      this.snackbarNotificationCustomButton = customButton
      this.snackbarNotificationCustomCloseButton = customCloseButton
      this.dismissLivestreamSnackbar({ dismissForOtherSnackbar: true })

      this.snackbarNotificationMessage = DOMPurify.sanitize(message, { ALLOWED_TAGS: ["b"] })
      await this.$nextTick()

      this.snackbarNotificationDisplayed = true
    },
    onCustomNotificationCloseButtonClick() {
      this.snackbarNotificationCustomCloseButton.clickCallback()
      this.hideSnackbarNotification()
    },
    async hideSnackbarNotification() {
      this.snackbarNotificationDisplayed = false

      // So the snackbar's text doesn't disapper before the fade out animation ends
      await sleep(300)
      this.snackbarNotificationMessage = null
    },
    async showSnackbarError(data) {
      let message, offset, contactSupportButtonDisplayed

      if (typeof data === "string") {
        message = data
      } else {
        message = data.message
        offset = data.offset || 20
        contactSupportButtonDisplayed = data.contactSupportButtonDisplayed
      }

      this.snackbarErrorDisplayed = false
      this.snackbarErrorOffset = offset
      this.snackbarNotificationDisplayed = false
      this.dismissLivestreamSnackbar({ dismissForOtherSnackbar: true })

      this.snackbarErrorContactSupportButtonDisplayed = contactSupportButtonDisplayed
      this.snackbarErrorMessage = message
      await this.$nextTick()

      this.snackbarErrorDisplayed = true
    },
    async hideSnackbarError() {
      this.snackbarErrorDisplayed = false

      // So the snackbar's text doesn't disapper before the fade out animation ends
      await sleep(300)
      this.snackbarErrorMessage = null
    },
    notifyLivestreamPreparing(data) {
      this.notifyLivestream({ ...data, state: eventTypes.preparing })
    },
    notifyLivestreamStreaming(data) {
      this.notifyLivestream({ ...data, state: eventTypes.streaming })
    },
    async notifyLivestream({ id, groupKey, eventId, state }) {
      if (
        (groupKey && !this.isUserMember(groupKey)) ||
        this.isAppLockedForUser ||
        !this.isLoggedIn ||
        this.webViewMode ||
        [this.$routes.RTMP, this.$routes.LIVE, this.$routes.BROADCAST, this.$routes.WALKTHROUGH].includes(this.$route.name)
      ) {
        return
      }

      if (!this.snackbarErrorDisplayed && !this.snackbarNotificationDisplayed) {
        this.dismissedForOtherSnackbarsLivestreamIds = []
      }

      if (this.manuallyDismissedLivestreamIds.includes(id) || this.dismissedForOtherSnackbarsLivestreamIds.includes(id)) {
        return
      }

      if (eventId && this.livestreamEvent?.id !== eventId) {
        const { event } = await eventsApi.get(eventId)
        this.livestreamEvent = event
      } else if (!eventId) {
        this.livestreamEvent = null
      }

      this.snackbarErrorDisplayed = false
      this.snackbarNotificationDisplayed = false
      this.snackbarRelatedLivestream = { id, groupKey, eventId, state }
    },
    hideLivestreamSnackbar({ id }) {
      if (this.snackbarRelatedLivestream?.id === id) {
        this.snackbarRelatedLivestream = null
      }
    },
    dismissLivestreamSnackbar({ dismissForOtherSnackbar } = {}) {
      if (dismissForOtherSnackbar) {
        this.dismissedForOtherSnackbarsLivestreamIds.push(this.snackbarRelatedLivestream?.id)
      } else {
        this.manuallyDismissedLivestreamIds.push(this.snackbarRelatedLivestream?.id)
      }
      this.snackbarRelatedLivestream = null
    }
  }
}
</script>

<style lang="scss" scoped>
.error-snackbar {
  :deep(.v-snackbar__wrapper) {
    background-color: var(--color-error-red) !important;
  }

  .v-btn {
    color: var(--color-white);
    border: 1px solid var(--color-white);
  }
}

.livestream-snackbar {
  :deep(.v-snackbar__wrapper) {
    background-color: var(--post-background);
    color: var(--post-text);

    .v-snackbar__content {
      padding: 8px 8px;
    }
  }

  &.desktop {
    :deep(.v-snackbar__content) {
      min-width: 350px;
    }
  }

  .livestream-title {
    max-width: 100%;
  }

  .v-btn {
    width: 24px !important;
    min-width: 24px !important;
    height: 24px !important;

    svg {
      width: 10px;
      height: 10px;
    }
  }

  .event-default-thumbnail {
    width: 64px;
    height: 64px;
  }

  .state-container {
    border: 1px solid;
  }

  .live-dot {
    color: red;
  }
}
</style>
